// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import Polygon from '@arcgis/core/geometry/Polygon';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import { CHART_COLORS_DARK } from 'Utils/chartColors';
// Pure field-resolution + filter predicates live in their own Esri-free module so they can be unit-tested.
import { resolveField, matchesFilter, preferSnapshot } from './metricFilters';
import { formatMeasure, isLengthUnit } from './widgetUnits';

// Client-side port of the standalone dashboard_metrics: each widget reads the geodatabase
// layers ALREADY loaded in the Project Viewer (GeoJSONLayers/FeatureLayers) and returns a small,
// render-ready structure. "Current view only" restricts every query to the map's current extent.
// Lengths/areas are computed with the ArcGIS geometryEngine (geodesic) in feet/ft².

type QueryableLayer = FeatureLayer | GeoJSONLayer;
type AnyView = MapView | SceneView;

// A metric that fails (bad field name, layer not ready, geometry error) falls back to an empty/0/null
// value — which then renders as a real "0" or a blank widget, indistinguishable from genuinely-no-data.
// Log the cause so a wrong-looking widget is diagnosable instead of silent.
// eslint-disable-next-line no-console
const warnMetric = (where: string, err: unknown): void => console.warn(`[khora dashboard] ${where} failed`, err);

export type WidgetKind =
  | 'kpi' | 'bar' | 'hist' | 'donut' | 'treemap' | 'radial' | 'radar' | 'scatter'
  | 'bubble' | 'waterfall' | 'stacked' | 'facts' | 'table' | 'heatmap';
export type Persona = 'Telecom' | 'AEC' | 'Site';

export interface WidgetDef {
  id: string;
  title: string;
  persona: Persona;
  kind: WidgetKind;
  requires?: string; // curated widgets only render when this layer exists
}

// What the features behind a clicked chart datapoint are, so we can highlight them on the map.
// `null` in a selectors[] marks a datapoint that isn't meaningfully highlightable (e.g. a computed
// "Other" remainder). Histograms instead carry a `source` (the clicked bin's range is derived from
// the values at click time).
export type ChartSelector =
  // 'layers' = every feature of these layers; 'category' = features where field == value;
  // 'feature' = one specific feature (by objectId).
  | { type: 'layers'; layers: string[] }
  | { type: 'category'; layers: string[]; field: string; value: string }
  | { type: 'feature'; layer: string; objectId: number };

export type WidgetData =
  | { kind: 'kpi'; value: string; help: string }
  | { kind: 'bar'; x: string[]; y: number[]; xTitle: string; yTitle: string; horizontal?: boolean; colors?: string[]; selectors?: (ChartSelector | null)[] }
  | { kind: 'hist'; values: number[]; xTitle: string; source?: { layer: string; field: string } }
  | { kind: 'donut'; labels: string[]; values: number[]; colors?: string[]; selectors?: (ChartSelector | null)[] }
  | { kind: 'treemap'; nodes: { name: string; value: number }[]; valueTitle: string; colors?: string[] }
  | { kind: 'radial'; value: number; max: number; valueLabel: string; help: string }
  | { kind: 'radar'; axes: string[]; values: number[]; valueTitle: string }
  | { kind: 'scatter'; points: { x: number; y: number }[]; xTitle: string; yTitle: string; selectors?: (ChartSelector | null)[] }
  | { kind: 'bubble'; points: { x: number; y: number; z: number }[]; xTitle: string; yTitle: string; zTitle: string; selectors?: (ChartSelector | null)[] }
  | { kind: 'waterfall'; steps: { label: string; value: number; isTotal: boolean }[]; valueTitle: string }
  | { kind: 'stacked'; categories: string[]; series: { name: string; values: number[]; color: string }[]; valueTitle: string }
  | { kind: 'facts'; items: { label: string; value: string }[] }
  | { kind: 'table'; rows: Record<string, string | number>[] }
  // 2D histogram / heatmap: matrix[rowIndex][colIndex] = count; rows/cols are the axis labels.
  | { kind: 'heatmap'; rows: string[]; cols: string[]; matrix: number[][]; rowTitle: string; colTitle: string; valueTitle: string };

export interface MetricsContext {
  layers: Map<string, QueryableLayer>; // by layer name
  view: AnyView;
  viewOnly: boolean;
}

const SQFT_PER_ACRE = 43560;
const ROAD_CLEARANCE_MIN_FT = 18;

const WIRE_LAYERS: [string, string][] = [
  ['Comm', 'T_COMM_WIRE'], ['Power', 'T_POWER_WIRE'],
  ['Guy', 'T_GUY_WIRE'], ['Guy OH', 'T_GUY_OVERHEAD'],
];
const ATTACHMENT_LAYERS = ['T_COMM_POA', 'T_POWER_POA', 'T_GUY_POA'];
const DRIVEWAY_LAYERS: [string, string][] = [
  ['Asphalt', 'R_DRIVEWAY_ASPH'], ['Concrete', 'R_DRIVEWAY_CONC'], ['Unpaved', 'R_DRIVEWAY_UNPAVED'],
];
// Impervious surfaces grouped into the steps of the site-area waterfall. The flat list
// (union of every group) is the single source of truth for total impervious area.
const IMPERVIOUS_GROUPS: [string, string[]][] = [
  ['Roads/Parking', ['R_ROAD_ASPH', 'R_PARKING_ASPH']],
  ['Driveways', ['R_DRIVEWAY_ASPH', 'R_DRIVEWAY_CONC']],
  ['Walkways', ['R_WALK_ASPH', 'R_WALK_CONC', 'R_WALK_PAVER']],
  ['Pads/Walls', ['C_CONC_PAD', 'S_WALL_CONC']],
];
const IMPERVIOUS_LAYERS = IMPERVIOUS_GROUPS.flatMap(([, layers]) => layers);

// --------------------------------------------------------------------------- //
// Layer access + aggregation helpers                                          //
// --------------------------------------------------------------------------- //

// Pull the queryable layers out of the viewer's geodatabase GroupLayers, keyed by name.
export const buildLayerMap = (groups: GroupLayer[]): Map<string, QueryableLayer> => {
  const map = new Map<string, QueryableLayer>();
  (groups || []).forEach((group) => {
    const children = group?.layers ? group.layers.toArray() : [group];
    children.forEach((layer) => {
      if (layer instanceof FeatureLayer || layer instanceof GeoJSONLayer) {
        map.set(layer.title || (layer as { name?: string }).name || '', layer);
      }
    });
  });
  return map;
};

// `includeGeometry` defaults true (measures/highlights need geometry). Attribute-only aggregates
// (count_by, average/sum/min/max) pass false so a large layer doesn't ship its full geometry + Z
// ordinates across the wire and into memory just to read one attribute.
const queryFeatures = async (ctx: MetricsContext, layerName: string, fields: string[] = ['*'], includeGeometry = true) => {
  const layer = ctx.layers.get(layerName);
  if (!layer) return [];
  const query = layer.createQuery();
  query.outFields = fields;
  query.returnGeometry = includeGeometry;
  // Esri defaults returnZ to false, so a hasZ layer (e.g. the paired-LineString poles in T_UP_BOTTOM)
  // comes back 2D and the vertex-Z height extraction sees nothing. Ask for Z (only when we fetch
  // geometry at all) so it actually arrives.
  query.returnZ = includeGeometry;
  if (ctx.viewOnly && ctx.view.extent) {
    query.geometry = ctx.view.extent;
    query.spatialRelationship = 'intersects';
  } else {
    query.where = '1=1';
  }
  try {
    await layer.load();
    const result = await layer.queryFeatures(query);
    return result.features;
  } catch (err) {
    warnMetric('queryFeatures', err);
    return [];
  }
};

const count = async (ctx: MetricsContext, layerName: string): Promise<number> => {
  const layer = ctx.layers.get(layerName);
  if (!layer) return 0;
  const query = layer.createQuery();
  if (ctx.viewOnly && ctx.view.extent) {
    query.geometry = ctx.view.extent;
    query.spatialRelationship = 'intersects';
  } else {
    query.where = '1=1';
  }
  try {
    return await layer.queryFeatureCount(query);
  } catch (err) {
    warnMetric('queryFeatureCount', err);
    return 0;
  }
};

// In "current view only" mode, clip each feature to the visible extent so a road/parcel that
// only overlaps the edge contributes just the portion inside the view — not its whole geometry.
const viewClip = (ctx: MetricsContext): Polygon | null => (
  ctx.viewOnly && ctx.view.extent ? Polygon.fromExtent(ctx.view.extent) : null
);

const clipped = (geometry: __esri.Geometry, clip: Polygon | null): __esri.Geometry | null => {
  if (!clip) return geometry;
  try {
    return geometryEngine.intersect(geometry, clip) as __esri.Geometry | null;
  } catch (err) {
    warnMetric('clip geometry', err);
    return null;
  }
};

// Per-feature geodesic lengths (ft) / areas (ft²), clipped to the view in view-only mode. The sum
// helpers build on these so any one feature is measured exactly one way everywhere, and the
// distribution/largest-feature widgets can reuse the same per-feature numbers.
const featureLengthsFt = async (ctx: MetricsContext, layerNames: string[]): Promise<number[]> => {
  const clip = viewClip(ctx);
  const lengths: number[] = [];
  await Promise.all(layerNames.map(async (name) => {
    const features = await queryFeatures(ctx, name, []);
    features.forEach((feature) => {
      const geometry = feature.geometry && clipped(feature.geometry, clip);
      if (geometry) lengths.push(geometryEngine.geodesicLength(geometry, 'feet'));
    });
  }));
  return lengths;
};

const featureAreasSqft = async (ctx: MetricsContext, layerNames: string[]): Promise<number[]> => {
  const clip = viewClip(ctx);
  const areas: number[] = [];
  await Promise.all(layerNames.map(async (name) => {
    const features = await queryFeatures(ctx, name, []);
    features.forEach((feature) => {
      const geometry = feature.geometry && clipped(feature.geometry, clip);
      if (geometry) areas.push(Math.abs(geometryEngine.geodesicArea(geometry as __esri.Polygon, 'square-feet')));
    });
  }));
  return areas;
};

const sumLengthFt = async (ctx: MetricsContext, layerNames: string[]): Promise<number> => (
  (await featureLengthsFt(ctx, layerNames)).reduce((total, length) => total + length, 0)
);

const sumAreaSqft = async (ctx: MetricsContext, layerNames: string[]): Promise<number> => (
  (await featureAreasSqft(ctx, layerNames)).reduce((total, area) => total + area, 0)
);

const numbersAttr = async (ctx: MetricsContext, layerName: string, ...candidates: string[]): Promise<number[]> => {
  const field = resolveField(ctx.layers.get(layerName), ...candidates);
  // Query ALL fields, not [field]: published GeoJSON renames fields (e.g. "Height (Ft)") and an
  // outFields restricted to a name with spaces/parens can come back empty — read it from the full row.
  // No geometry: this only reads a numeric attribute.
  const features = await queryFeatures(ctx, layerName, ['*'], false);
  return features
    .map((feature) => feature.attributes?.[field])
    .filter((value): value is number => typeof value === 'number');
};

// Pole heights (ft). T_UP_BOTTOM publishes as a paired 3D LineString, so the height is the vertex
// Z-extent (meters -> feet); fall back to the published Height (Ft) attribute. Both are reliable;
// reading the geometry sidesteps the renamed/special-char field name entirely.
const poleHeightsFt = async (ctx: MetricsContext): Promise<number[]> => {
  const field = resolveField(ctx.layers.get('T_UP_BOTTOM'), 'Height_ft');
  const features = await queryFeatures(ctx, 'T_UP_BOTTOM');
  const heights: number[] = [];
  features.forEach((feature) => {
    const geometry = feature.geometry as __esri.Polyline | undefined;
    if (geometry?.type === 'polyline' && geometry.hasZ) {
      const zs = geometry.paths.flat().map((vertex) => vertex[2]).filter((z): z is number => typeof z === 'number');
      if (zs.length >= 2) {
        const extentFt = (Math.max(...zs) - Math.min(...zs)) * 3.28084;
        if (extentFt > 0) { heights.push(extentFt); return; }
      }
    }
    const attr = feature.attributes?.[field];
    if (typeof attr === 'number' && attr > 0) heights.push(attr);
  });
  return heights;
};

const fmt = (value: number, digits = 0): string => value.toLocaleString('en-US', {
  minimumFractionDigits: digits, maximumFractionDigits: digits,
});

// Format a scalar aggregate (average/sum/min/max of a *_ft field, or a plain count) honoring a non-feet
// LENGTH display_unit. The value is in feet (the aggregate's base); a length display_unit converts + labels
// it to match Khora's chat answer, otherwise the bare number is shown — a count is dimensionless and a
// plain-feet aggregate has no server-declared unit, so neither should be labeled or lose precision.
const formatScalar = (value: number, displayUnit?: string): string => {
  if (displayUnit && displayUnit !== 'ft' && isLengthUnit(displayUnit)) {
    return formatMeasure(value, 'length', displayUnit);
  }
  return fmt(value, Number.isInteger(value) ? 0 : 1);
};

// --------------------------------------------------------------------------- //
// Map-consistent colors                                                       //
// --------------------------------------------------------------------------- //
// Pull each layer's rendered color so a chart bar/slice matches its symbology in the viewer
// (e.g. the S_FENCE bar uses S_FENCE's map color). Aggregate/conglomerate series — that roll
// up several layers (impervious surface, "Other") — use neutral grays instead.

// The dark-panel AirWorks brand palette (Bright Blue #01A1DD + Bright Orange #FF9424 lead; the rest
// stay light so multi-category charts read on the dark dashboard). Shared with the panel.
const FALLBACK_COLORS = CHART_COLORS_DARK;
// Neutral grays for aggregate/roll-up series; the light one is the brand Light Gray #D9D9D9.
const NEUTRAL_COLOR = '#8C97A1';
const NEUTRAL_LIGHT = '#D9D9D9';

interface RendererWithSymbol { symbol?: { color?: { toHex?: () => string } | null } | null }

// A layer's display color from its (simple) renderer symbol, as a hex string, or null.
const layerColor = (layer?: QueryableLayer): string | null => {
  if (!layer) return null;
  const renderer = layer.renderer as unknown as RendererWithSymbol | null;
  return renderer?.symbol?.color?.toHex?.() || null;
};

// Color for a named layer: its map symbology when available, else a palette fallback by index.
const colorForLayer = (ctx: MetricsContext, name: string, fallbackIndex: number): string => (
  layerColor(ctx.layers.get(name)) ?? FALLBACK_COLORS[fallbackIndex % FALLBACK_COLORS.length]
);

// --------------------------------------------------------------------------- //
// Generic widgets — work for ANY geodatabase (built from the layer inventory)  //
// --------------------------------------------------------------------------- //

// Resolve a layer's geometry type robustly: read it after loading; if still unset, infer it from a
// sample feature's geometry (layer.geometryType can be null even when the layer has real geometry).
const sampleGeometryType = async (ctx: MetricsContext, name: string): Promise<string | null> => {
  const layer = ctx.layers.get(name);
  if (!layer) return null;
  try {
    if (!layer.loaded) await layer.load();
    if (layer.geometryType) return layer.geometryType;
    const query = layer.createQuery();
    query.where = '1=1';
    query.num = 1;
    query.returnGeometry = true;
    query.outFields = [];
    const result = await layer.queryFeatures(query);
    return result.features[0]?.geometry?.type ?? null;
  } catch (err) {
    warnMetric('geometryType', err);
    return null;
  }
};

const layerInventory = async (ctx: MetricsContext): Promise<{ name: string; geometryType: string; count: number }[]> => {
  const names = Array.from(ctx.layers.keys());
  return Promise.all(names.map(async (name) => ({
    name,
    geometryType: (await sampleGeometryType(ctx, name)) || 'table',
    count: await count(ctx, name),
  })));
};

// ============================================================================================
// LEGACY — client-side AUTO-WIDGET subsystem. FROZEN, do not extend.
// ============================================================================================
// Everything from here through `computeWidget` (the g*/tele*/aec*/site*/dataQuality/vegEncroachment
// widget functions, their private helpers, the COMPUTE registry, GENERIC_WIDGETS/CURATED_WIDGETS,
// applicableWidgets/computeWidget), PLUS focusedDistribution/focusedRelationship/focusedWidgets below,
// is the OLD auto board: it derives widgets from the raw gdb layers with NO Khora input. It runs ONLY
// when `LEGACY_AUTO_WIDGETS` is true (currently false), and is kept purely so the old behavior can be
// restored. Khora-driven dashboard content goes through `computeSpec` (further down) — build new work
// there, NOT here. Do not extend or "improve" this block.
const gTotalFeatures = async (ctx: MetricsContext): Promise<WidgetData> => {
  const inv = await layerInventory(ctx);
  const total = inv.reduce((sum, layer) => sum + layer.count, 0);
  return { kind: 'kpi', value: fmt(total), help: `Total features across ${inv.length} layers` };
};

const gLayers = async (ctx: MetricsContext): Promise<WidgetData> => {
  const inv = await layerInventory(ctx);
  if (ctx.viewOnly) {
    const inView = inv.filter((layer) => layer.count > 0).length;
    return { kind: 'kpi', value: `${inView}`, help: `Layers with features in view (of ${inv.length})` };
  }
  return { kind: 'kpi', value: `${inv.length}`, help: `${inv.length} mappable layers` };
};

const gTopLayers = async (ctx: MetricsContext): Promise<WidgetData> => {
  const inv = (await layerInventory(ctx)).sort((a, b) => b.count - a.count).slice(0, 8);
  // Horizontal so the full layer names stay readable; each bar uses its layer's map color.
  return {
    kind: 'bar',
    x: inv.map((l) => l.name),
    y: inv.map((l) => l.count),
    xTitle: 'Layer',
    yTitle: 'Features',
    horizontal: true,
    colors: inv.map((l, i) => colorForLayer(ctx, l.name, i)),
    selectors: inv.map((l): ChartSelector => ({ type: 'layers', layers: [l.name] })),
  };
};

// Treemap of every layer sized by feature count — a quick read on what dominates the gdb.
const gComposition = async (ctx: MetricsContext): Promise<WidgetData> => {
  const inv = (await layerInventory(ctx))
    .filter((layer) => layer.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 14);
  return {
    kind: 'treemap',
    nodes: inv.map((layer) => ({ name: layer.name, value: layer.count })),
    valueTitle: 'Features',
    colors: inv.map((layer, i) => colorForLayer(ctx, layer.name, i)),
  };
};

const gGeometryTypes = async (ctx: MetricsContext): Promise<WidgetData> => {
  const inv = await layerInventory(ctx);
  const counts = new Map<string, number>();
  inv.forEach((layer) => {
    if (ctx.viewOnly && layer.count === 0) return;
    const kind = (layer.geometryType || 'table').replace('multi', '').replace('-3d', '');
    counts.set(kind, (counts.get(kind) || 0) + 1);
  });
  return { kind: 'donut', labels: Array.from(counts.keys()), values: Array.from(counts.values()) };
};

const CATEGORY_PREFIXES: [string, string][] = [
  ['T_', 'Telecom'], ['U_', 'Utilities'], ['R_', 'Roads/Pavement'],
  ['S_', 'Structures'], ['V_', 'Vegetation'], ['W_', 'Water'], ['A_', 'Boundary'],
];

// Feature counts rolled up by layer-name prefix into domain categories (non-zero only).
const categoryTally = async (ctx: MetricsContext): Promise<[string, number][]> => {
  const tally = new Map<string, number>(CATEGORY_PREFIXES.map(([, label]) => [label, 0]));
  const inv = await layerInventory(ctx);
  inv.forEach((layer) => {
    const match = CATEGORY_PREFIXES.find(([prefix]) => layer.name.startsWith(prefix));
    if (match) tally.set(match[1], (tally.get(match[1]) || 0) + layer.count);
  });
  return Array.from(tally.entries()).filter(([, value]) => value > 0);
};

const gCategories = async (ctx: MetricsContext): Promise<WidgetData> => {
  const rows = (await categoryTally(ctx)).map(([category, features]) => ({ Category: category, Features: features }));
  return { kind: 'table', rows };
};

// Same category tally as a radar — a quick visual of how the gdb is weighted across domains.
const gCategoryRadar = async (ctx: MetricsContext): Promise<WidgetData> => {
  const entries = await categoryTally(ctx);
  return { kind: 'radar', axes: entries.map(([k]) => k), values: entries.map(([, v]) => v), valueTitle: 'Features' };
};

const GEOM_ORDER = ['point', 'polyline', 'polygon', 'table'];
const GEOM_LABEL: Record<string, string> = {
  point: 'Point', polyline: 'Line', polygon: 'Polygon', table: 'Table',
};
const normalizeGeometry = (geometryType: string): string => (
  (geometryType || 'table').replace('multi', '').replace('-3d', '')
);

// Stacked 100% bar: for each domain category, the share of its features that are points vs lines
// vs polygons — "components of components" (Abela), normalized in the renderer to 100%.
const gCategoryStacked = async (ctx: MetricsContext): Promise<WidgetData> => {
  const inv = await layerInventory(ctx);
  const tally = new Map<string, Map<string, number>>();
  const present = new Set<string>();
  inv.forEach((layer) => {
    const match = CATEGORY_PREFIXES.find(([prefix]) => layer.name.startsWith(prefix));
    if (!match || layer.count === 0) return;
    const geometry = normalizeGeometry(layer.geometryType);
    present.add(geometry);
    const row = tally.get(match[1]) ?? new Map<string, number>();
    row.set(geometry, (row.get(geometry) || 0) + layer.count);
    tally.set(match[1], row);
  });
  const categories = CATEGORY_PREFIXES.map(([, label]) => label).filter((label) => tally.has(label));
  const geometries = GEOM_ORDER.filter((geometry) => present.has(geometry));
  const series = geometries.map((geometry, index) => ({
    name: GEOM_LABEL[geometry] ?? geometry,
    values: categories.map((category) => tally.get(category)?.get(geometry) ?? 0),
    color: FALLBACK_COLORS[index % FALLBACK_COLORS.length],
  }));
  return { kind: 'stacked', categories, series, valueTitle: 'Features' };
};

// Number of coordinate vertices in a geometry — a proxy for how detailed/complex a feature is.
const vertexCount = (geometry: __esri.Geometry): number => {
  if (geometry.type === 'polygon') {
    return (geometry as __esri.Polygon).rings.reduce((sum, ring) => sum + ring.length, 0);
  }
  if (geometry.type === 'polyline') {
    return (geometry as __esri.Polyline).paths.reduce((sum, path) => sum + path.length, 0);
  }
  return 1;
};

// Names of non-empty layers of a given normalized geometry (point / polyline / polygon).
const layerNamesByGeometry = (
  inventory: { name: string; geometryType: string; count: number }[],
  geometry: string,
): string[] => inventory
  .filter((layer) => layer.count > 0 && normalizeGeometry(layer.geometryType) === geometry)
  .map((layer) => layer.name);

// The single largest feature across a set of layers, by geodesic area (polygons) or length (lines).
const biggestFeature = async (
  ctx: MetricsContext,
  layerNames: string[],
  measure: 'area' | 'length',
): Promise<{ layer: string; value: number } | null> => {
  const perLayer = await Promise.all(layerNames.map(async (name) => {
    const values = measure === 'area'
      ? await featureAreasSqft(ctx, [name])
      : await featureLengthsFt(ctx, [name]);
    return { layer: name, value: values.length ? Math.round(Math.max(...values)) : 0 };
  }));
  const ranked = perLayer.filter((entry) => entry.value > 0).sort((a, b) => b.value - a.value);
  return ranked[0] ?? null;
};

// "Interesting facts about the gdb" — a compact highlights list (respects the current-view toggle,
// so it describes whatever is in the entire view). Each fact is added only when computable.
const gHighlights = async (ctx: MetricsContext): Promise<WidgetData> => {
  const inv = await layerInventory(ctx);
  const nonEmpty = inv.filter((layer) => layer.count > 0);
  const items: { label: string; value: string }[] = [];

  const total = nonEmpty.reduce((sum, layer) => sum + layer.count, 0);
  items.push({ label: 'Features', value: `${fmt(total)} across ${nonEmpty.length} layers` });

  const largest = [...nonEmpty].sort((a, b) => b.count - a.count)[0];
  if (largest) items.push({ label: 'Largest layer', value: `${largest.name} (${fmt(largest.count)})` });

  const geomCounts = new Map<string, number>();
  nonEmpty.forEach((layer) => {
    const label = GEOM_LABEL[normalizeGeometry(layer.geometryType)] ?? 'Other';
    geomCounts.set(label, (geomCounts.get(label) || 0) + 1);
  });
  const dominant = Array.from(geomCounts.entries()).sort((a, b) => b[1] - a[1])[0];
  if (dominant) items.push({ label: 'Dominant geometry', value: `${dominant[0]} (${dominant[1]} layers)` });

  const biggestPolygon = await biggestFeature(ctx, layerNamesByGeometry(inv, 'polygon'), 'area');
  if (biggestPolygon) items.push({ label: 'Biggest polygon', value: `${biggestPolygon.layer} — ${fmt(biggestPolygon.value)} ft²` });

  const longestLine = await biggestFeature(ctx, layerNamesByGeometry(inv, 'polyline'), 'length');
  if (longestLine) items.push({ label: 'Longest line', value: `${longestLine.layer} — ${fmt(longestLine.value)} ft` });

  if (ctx.layers.has('T_UP_BOTTOM')) {
    const heights = await poleHeightsFt(ctx);
    if (heights.length) items.push({ label: 'Tallest pole', value: `${Math.max(...heights).toFixed(1)} ft` });
  }

  if (ctx.layers.has('A_BOUNDARY')) {
    const site = await siteAreaSqft(ctx);
    if (site > 0) {
      const impervious = await sumAreaSqft(ctx, IMPERVIOUS_LAYERS);
      items.push({ label: 'Site area', value: `${(site / SQFT_PER_ACRE).toFixed(2)} ac` });
      items.push({ label: 'Impervious cover', value: `${Math.round((impervious / site) * 100)}% of site` });
    }
  }

  return { kind: 'facts', items };
};

// Largest polygon layers by total geodesic area, in acres (horizontal so names stay readable).
const gLargestArea = async (ctx: MetricsContext): Promise<WidgetData> => {
  const inv = await layerInventory(ctx);
  const polygonNames = layerNamesByGeometry(inv, 'polygon');
  const areas = await Promise.all(polygonNames.map(async (name) => [name, await sumAreaSqft(ctx, [name])] as const));
  const top = areas.filter(([, area]) => area > 0).sort((a, b) => b[1] - a[1]).slice(0, 8);
  return {
    kind: 'bar',
    x: top.map(([name]) => name),
    y: top.map(([, area]) => Math.round((area / SQFT_PER_ACRE) * 100) / 100),
    xTitle: 'Layer',
    yTitle: 'Area (acres)',
    horizontal: true,
    colors: top.map(([name], index) => colorForLayer(ctx, name, index)),
    selectors: top.map(([name]): ChartSelector => ({ type: 'layers', layers: [name] })),
  };
};

// Distribution of individual polygon feature sizes across the gdb — how parcel/surface areas spread.
const gSizeDist = async (ctx: MetricsContext): Promise<WidgetData> => {
  const inv = await layerInventory(ctx);
  const areas = await featureAreasSqft(ctx, layerNamesByGeometry(inv, 'polygon'));
  return { kind: 'hist', values: areas, xTitle: 'Polygon area (ft²)' };
};

// Per-layer complexity: feature count vs average vertices/feature, sized by total vertices.
const gComplexity = async (ctx: MetricsContext): Promise<WidgetData> => {
  const inv = (await layerInventory(ctx))
    .filter((layer) => layer.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 12);
  const rows = await Promise.all(inv.map(async (layer) => {
    const features = await queryFeatures(ctx, layer.name, []);
    const vertices = features.reduce((sum, feature) => sum + (feature.geometry ? vertexCount(feature.geometry) : 0), 0);
    const avg = features.length ? Math.round(vertices / features.length) : 0;
    return { x: layer.count, y: avg, z: vertices, name: layer.name };
  }));
  const valid = rows.filter((row) => row.z > 0);
  if (valid.length < 3) throw new Error('insufficient geometry for complexity profile');
  return {
    kind: 'bubble',
    points: valid.map(({ x, y, z }) => ({ x, y, z })),
    xTitle: 'Feature count',
    yTitle: 'Avg vertices / feature',
    zTitle: 'Total vertices',
    selectors: valid.map((row): ChartSelector => ({ type: 'layers', layers: [row.name] })),
  };
};

const gInventory = async (ctx: MetricsContext): Promise<WidgetData> => {
  // A full inventory — list every layer (the table scrolls), never cap it at 30.
  const inv = (await layerInventory(ctx)).sort((a, b) => b.count - a.count);
  return {
    kind: 'table',
    rows: inv.map((layer) => ({ Layer: layer.name, Geometry: layer.geometryType || '—', Features: layer.count })),
  };
};

// --------------------------------------------------------------------------- //
// Curated telecom / AEC widgets                                               //
// --------------------------------------------------------------------------- //

const telePoles = async (ctx: MetricsContext): Promise<WidgetData> => (
  { kind: 'kpi', value: fmt(await count(ctx, 'T_UP_BOTTOM')), help: 'Utility poles (T_UP_BOTTOM)' }
);

const telePoleHeight = async (ctx: MetricsContext): Promise<WidgetData> => {
  const heights = await poleHeightsFt(ctx);
  const avg = heights.length ? heights.reduce((a, b) => a + b, 0) / heights.length : 0;
  const tallest = heights.length ? Math.max(...heights) : 0;
  return { kind: 'kpi', value: `${avg.toFixed(1)} ft`, help: `Average pole height — tallest ${tallest.toFixed(1)} ft` };
};

const teleWireLen = async (ctx: MetricsContext): Promise<WidgetData> => (
  { kind: 'kpi', value: `${fmt(await sumLengthFt(ctx, WIRE_LAYERS.map(([, l]) => l)))} ft`, help: 'Total wire length (comm + power + guy)' }
);

const teleAttach = async (ctx: MetricsContext): Promise<WidgetData> => {
  const counts = await Promise.all(ATTACHMENT_LAYERS.map((layer) => count(ctx, layer)));
  return { kind: 'kpi', value: fmt(counts.reduce((a, b) => a + b, 0)), help: 'Points of attachment (comm + power + guy)' };
};

const teleClearFlags = async (ctx: MetricsContext): Promise<WidgetData> => {
  const heights = await numbersAttr(ctx, 'ROAD_LINE_CLEARANCE', 'Height_ft');
  const flags = heights.filter((h) => h < ROAD_CLEARANCE_MIN_FT).length;
  return { kind: 'kpi', value: fmt(flags), help: `Road-line clearances below ${ROAD_CLEARANCE_MIN_FT} ft (NESC minimum)` };
};

// Gauge: share of road-line clearances meeting the NESC minimum.
const teleCompliance = async (ctx: MetricsContext): Promise<WidgetData> => {
  const heights = await numbersAttr(ctx, 'ROAD_LINE_CLEARANCE', 'Height_ft');
  const passing = heights.filter((h) => h >= ROAD_CLEARANCE_MIN_FT).length;
  const pct = heights.length ? (passing / heights.length) * 100 : 0;
  return {
    kind: 'radial',
    value: Math.round(pct),
    max: 100,
    valueLabel: `${Math.round(pct)}%`,
    help: `${fmt(passing)} of ${fmt(heights.length)} clearances at/above the ${ROAD_CLEARANCE_MIN_FT} ft NESC minimum`,
  };
};

const telePoleHist = async (ctx: MetricsContext): Promise<WidgetData> => ({
  kind: 'hist',
  values: await poleHeightsFt(ctx),
  xTitle: 'Pole height (ft)',
  source: { layer: 'T_UP_BOTTOM', field: resolveField(ctx.layers.get('T_UP_BOTTOM'), 'Height_ft') },
});

const teleWireBar = async (ctx: MetricsContext): Promise<WidgetData> => {
  const lengths = await Promise.all(WIRE_LAYERS.map(async ([label, layer]) => [label, await sumLengthFt(ctx, [layer])] as const));
  return {
    kind: 'bar',
    x: lengths.map(([l]) => l),
    y: lengths.map(([, v]) => Math.round(v)),
    xTitle: 'Wire type',
    yTitle: 'Length (ft)',
    colors: WIRE_LAYERS.map(([, layer], i) => colorForLayer(ctx, layer, i)),
    selectors: WIRE_LAYERS.map(([, layer]): ChartSelector => ({ type: 'layers', layers: [layer] })),
  };
};

const teleClearBands = async (ctx: MetricsContext): Promise<WidgetData> => {
  const bandField = resolveField(ctx.layers.get('ROAD_LINE_CLEARANCE'), 'Height_grp');
  const features = await queryFeatures(ctx, 'ROAD_LINE_CLEARANCE', [bandField]);
  const counts = new Map<string, number>();
  features.forEach((feature) => {
    const band = feature.attributes?.[bandField];
    if (band !== null && band !== undefined) counts.set(String(band), (counts.get(String(band)) || 0) + 1);
  });
  const bands = Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  return {
    kind: 'bar',
    x: bands.map(([b]) => b),
    y: bands.map(([, n]) => n),
    xTitle: 'Clearance band',
    yTitle: 'Crossings',
    selectors: bands.map(([band]): ChartSelector => ({ type: 'category', layers: ['ROAD_LINE_CLEARANCE'], field: bandField, value: band })),
  };
};

const teleLowSags = async (ctx: MetricsContext): Promise<WidgetData> => {
  const layer = ctx.layers.get('LOWEST_LINE_SAG');
  const heightField = resolveField(layer, 'Height_ft');
  const bandField = resolveField(layer, 'Height_grp');
  const features = await queryFeatures(ctx, 'LOWEST_LINE_SAG', [heightField, bandField]);
  const sorted = features
    .map((feature) => ({ h: feature.attributes?.[heightField] as number, band: feature.attributes?.[bandField] }))
    .filter((row) => typeof row.h === 'number')
    .sort((a, b) => a.h - b.h)
    .slice(0, 5);
  return {
    kind: 'table',
    rows: sorted.map((row, index) => ({ Rank: index + 1, 'Sag height (ft)': Math.round(row.h * 10) / 10, Band: row.band ?? '—' })),
  };
};

const siteAreaSqft = (ctx: MetricsContext) => sumAreaSqft(ctx, ['A_BOUNDARY']);

const aecSiteArea = async (ctx: MetricsContext): Promise<WidgetData> => {
  const sqft = await siteAreaSqft(ctx);
  return { kind: 'kpi', value: `${(sqft / SQFT_PER_ACRE).toFixed(2)} ac`, help: `Site boundary area (${fmt(sqft)} ft²)` };
};

const aecImpervious = async (ctx: MetricsContext): Promise<WidgetData> => {
  const impervious = await sumAreaSqft(ctx, IMPERVIOUS_LAYERS);
  const site = await siteAreaSqft(ctx);
  const pct = site ? (impervious / site) * 100 : 0;
  return { kind: 'kpi', value: `${fmt(impervious)} ft²`, help: `Impervious surface — ${pct.toFixed(0)}% of the site` };
};

// Gauge: impervious surface as a share of the site boundary.
const aecImperviousGauge = async (ctx: MetricsContext): Promise<WidgetData> => {
  const impervious = await sumAreaSqft(ctx, IMPERVIOUS_LAYERS);
  const site = await siteAreaSqft(ctx);
  const pct = site ? Math.min((impervious / site) * 100, 100) : 0;
  return {
    kind: 'radial',
    value: Math.round(pct),
    max: 100,
    valueLabel: `${Math.round(pct)}%`,
    help: `${fmt(impervious)} ft² impervious of ${fmt(site)} ft² site`,
  };
};

const aecBarriers = async (ctx: MetricsContext): Promise<WidgetData> => (
  { kind: 'kpi', value: `${fmt(await sumLengthFt(ctx, ['S_FENCE', 'S_RAILING', 'S_GUARDRAIL']))} ft`, help: 'Fence + railing + guardrail length' }
);

const aecDrainage = async (ctx: MetricsContext): Promise<WidgetData> => {
  const counts = await Promise.all(['U_CATCHBASIN', 'U_VALVE', 'U_HYDRANT'].map((layer) => count(ctx, layer)));
  return { kind: 'kpi', value: fmt(counts.reduce((a, b) => a + b, 0)), help: 'Drainage/utility points (catch basins + valves + hydrants)' };
};

const aecDrivewayBar = async (ctx: MetricsContext): Promise<WidgetData> => {
  const areas = await Promise.all(DRIVEWAY_LAYERS.map(async ([label, layer]) => [label, await sumAreaSqft(ctx, [layer])] as const));
  return {
    kind: 'bar',
    x: areas.map(([l]) => l),
    y: areas.map(([, v]) => Math.round(v)),
    xTitle: 'Driveway surface',
    yTitle: 'Area (ft²)',
    colors: DRIVEWAY_LAYERS.map(([, layer], i) => colorForLayer(ctx, layer, i)),
    selectors: DRIVEWAY_LAYERS.map(([, layer]): ChartSelector => ({ type: 'layers', layers: [layer] })),
  };
};

const aecCoverDonut = async (ctx: MetricsContext): Promise<WidgetData> => {
  const site = await siteAreaSqft(ctx);
  const impervious = await sumAreaSqft(ctx, IMPERVIOUS_LAYERS);
  const vegetation = await sumAreaSqft(ctx, ['V_VEGETATION']);
  const other = Math.max(site - impervious - vegetation, 0);
  return {
    kind: 'donut',
    labels: ['Impervious', 'Vegetation', 'Other'],
    values: [Math.round(impervious), Math.round(vegetation), Math.round(other)],
    // Impervious + Other roll up many layers -> neutral; Vegetation maps to V_VEGETATION's color.
    colors: [NEUTRAL_COLOR, layerColor(ctx.layers.get('V_VEGETATION')) ?? '#81c784', NEUTRAL_LIGHT],
    // 'Other' is a computed remainder (site minus impervious/vegetation) — no features to highlight.
    selectors: [{ type: 'layers', layers: IMPERVIOUS_LAYERS }, { type: 'layers', layers: ['V_VEGETATION'] }, null],
  };
};

// Waterfall: start at the full site area, step down through each impervious surface group, and
// land on the pervious remainder — a running-balance read on how the site area is consumed.
const aecImperviousWaterfall = async (ctx: MetricsContext): Promise<WidgetData> => {
  const site = await siteAreaSqft(ctx);
  const groupAreas = await Promise.all(
    IMPERVIOUS_GROUPS.map(async ([label, layers]) => [label, await sumAreaSqft(ctx, layers)] as const),
  );
  const steps: { label: string; value: number; isTotal: boolean }[] = [
    { label: 'Site', value: Math.round(site), isTotal: true },
  ];
  let impervious = 0;
  groupAreas.forEach(([label, area]) => {
    if (area <= 0) return;
    impervious += area;
    steps.push({ label, value: -Math.round(area), isTotal: false });
  });
  steps.push({ label: 'Pervious', value: Math.round(Math.max(site - impervious, 0)), isTotal: true });
  return { kind: 'waterfall', steps, valueTitle: 'Area (ft²)' };
};

// Pole make-ready loading, read from T_UP_BOTTOM's inline POA_1..POA_20 columns (each pole carries up
// to 20 points-of-attachment with an ID + type). Returns attachments-per-pole and a type breakdown.
const POA_SLOTS = Array.from({ length: 20 }, (_, index) => index + 1);
const poleAttachments = async (ctx: MetricsContext): Promise<{ perPole: number[]; byType: Map<string, number> }> => {
  const features = await queryFeatures(ctx, 'T_UP_BOTTOM');
  const perPole: number[] = [];
  const byType = new Map<string, number>();
  features.forEach((feature) => {
    const attrs = feature.attributes || {};
    let attached = 0;
    POA_SLOTS.forEach((slot) => {
      const id = attrs[`POA_${slot}_ID`];
      if (id === null || id === undefined || id === '') return;
      attached += 1;
      const type = attrs[`POA_${slot}_Type`];
      const key = type ? String(type) : 'Unknown';
      byType.set(key, (byType.get(key) || 0) + 1);
    });
    perPole.push(attached);
  });
  return { perPole, byType };
};

const telePoleLoading = async (ctx: MetricsContext): Promise<WidgetData> => {
  const { perPole } = await poleAttachments(ctx);
  if (!perPole.length) throw new Error('no poles');
  const total = perPole.reduce((a, b) => a + b, 0);
  return {
    kind: 'kpi',
    value: (total / perPole.length).toFixed(1),
    help: `Avg attachments per pole — busiest carries ${Math.max(...perPole)}`,
  };
};

const teleAttachTypes = async (ctx: MetricsContext): Promise<WidgetData> => {
  const { byType } = await poleAttachments(ctx);
  const entries = Array.from(byType.entries()).sort((a, b) => b[1] - a[1]);
  if (!entries.length) throw new Error('no attachments');
  return { kind: 'bar', x: entries.map((e) => e[0]), y: entries.map((e) => e[1]), xTitle: 'Attachment type', yTitle: 'Count' };
};

const teleAttachDist = async (ctx: MetricsContext): Promise<WidgetData> => {
  const { perPole } = await poleAttachments(ctx);
  if (!perPole.length) throw new Error('no poles');
  return { kind: 'hist', values: perPole, xTitle: 'Attachments per pole' };
};

// Attachment height × type heatmap (a 2D histogram): every pole attachment binned by its height
// (POA_*_Ht) and grouped by type (POA_*_Type), so you can see where each attachment type rides on
// the poles. Cell = number of attachments in that type+height cell.
const teleAttachHeatmap = async (ctx: MetricsContext): Promise<WidgetData> => {
  const features = await queryFeatures(ctx, 'T_UP_BOTTOM');
  const attachments: { type: string; height: number }[] = [];
  features.forEach((feature) => {
    const attrs = feature.attributes || {};
    POA_SLOTS.forEach((slot) => {
      const id = attrs[`POA_${slot}_ID`];
      if (id === null || id === undefined || id === '') return;
      const height = attrs[`POA_${slot}_Ht`];
      if (typeof height !== 'number') return;
      const type = attrs[`POA_${slot}_Type`];
      attachments.push({ type: type ? String(type) : 'Unknown', height });
    });
  });
  if (attachments.length < 4) throw new Error('not enough attachments for a heatmap');

  // Columns = 5 equal-width height bands across the observed range.
  const heights = attachments.map((attachment) => attachment.height);
  const min = Math.min(...heights);
  const max = Math.max(...heights);
  const binCount = 5;
  const width = (max - min) / binCount || 1;
  const cols = Array.from({ length: binCount }, (_, i) => `${(min + i * width).toFixed(0)}–${(min + (i + 1) * width).toFixed(0)}`);

  // Rows = attachment types, most common first — show every type (never drop one from the grid/table).
  const typeTotals = new Map<string, number>();
  attachments.forEach((attachment) => typeTotals.set(attachment.type, (typeTotals.get(attachment.type) || 0) + 1));
  const rows = Array.from(typeTotals.entries()).sort((a, b) => b[1] - a[1]).map((entry) => entry[0]);
  const rowIndex = new Map(rows.map((type, i) => [type, i]));

  const matrix = rows.map(() => Array.from({ length: binCount }, () => 0));
  attachments.forEach((attachment) => {
    const ri = rowIndex.get(attachment.type);
    if (ri === undefined) return;
    const ci = Math.min(binCount - 1, Math.floor((attachment.height - min) / width));
    matrix[ri][ci] += 1;
  });

  return { kind: 'heatmap', rows, cols, matrix, rowTitle: 'Attachment type', colTitle: 'Height (ft)', valueTitle: 'Attachments' };
};

// NESC vertical-clearance violations: road-line clearances below the minimum, worst first, with the
// shortfall. Surfaces the actual punch-list, not just a count. (The clearance-bands bar is already
// click-to-highlight, so the low band highlights the failing crossings on the map.)
const teleViolations = async (ctx: MetricsContext): Promise<WidgetData> => {
  const heights = await numbersAttr(ctx, 'ROAD_LINE_CLEARANCE', 'Height_ft');
  // Show EVERY violation, worst first — this is an NESC safety punch-list; never hide a failing crossing.
  const violations = heights.filter((height) => height < ROAD_CLEARANCE_MIN_FT).sort((a, b) => a - b);
  if (!violations.length) throw new Error('no clearance violations');
  return {
    kind: 'table',
    rows: violations.map((height, index) => ({
      '#': index + 1,
      'Clearance (ft)': Math.round(height * 10) / 10,
      'Below min (ft)': Math.round((ROAD_CLEARANCE_MIN_FT - height) * 10) / 10,
    })),
  };
};

// Data-quality flags: anomalies a surveyor/QA would want to see (missing pole heights, empty layers).
// Turns silent gaps into a visible signal rather than wrong/zero numbers elsewhere.
const dataQuality = async (ctx: MetricsContext): Promise<WidgetData> => {
  const items: { label: string; value: string }[] = [];
  if (ctx.layers.has('T_UP_BOTTOM')) {
    const [poles, heights] = await Promise.all([count(ctx, 'T_UP_BOTTOM'), poleHeightsFt(ctx)]);
    if (poles - heights.length > 0) items.push({ label: 'Poles missing a height', value: `${poles - heights.length}` });
  }
  const emptyLayers = (await layerInventory(ctx)).filter((layer) => layer.count === 0);
  if (emptyLayers.length) items.push({ label: 'Layers with no features', value: `${emptyLayers.length}` });
  if (!items.length) items.push({ label: 'Checks', value: 'No data-quality issues found' });
  return { kind: 'facts', items };
};

// Vegetation encroachment: wire spans running within VEG_ENCROACH_FT of vegetation — a core
// make-ready/maintenance signal. Buffers the (unioned) vegetation and intersects wires against it.
const VEG_ENCROACH_FT = 10;
const vegEncroachment = async (ctx: MetricsContext): Promise<WidgetData> => {
  if (!ctx.layers.has('V_VEGETATION')) throw new Error('no vegetation layer');
  const vegFeatures = await queryFeatures(ctx, 'V_VEGETATION');
  const vegGeoms = vegFeatures.map((feature) => feature.geometry).filter((geom): geom is NonNullable<typeof geom> => geom != null);
  if (!vegGeoms.length) throw new Error('no vegetation geometry');
  const union = geometryEngine.union(vegGeoms) as __esri.GeometryUnion;
  const buffer = geometryEngine.geodesicBuffer(union, VEG_ENCROACH_FT, 'feet') as __esri.Polygon | null;
  if (!buffer) throw new Error('vegetation buffer failed');
  const perLayer = await Promise.all(WIRE_LAYERS.map(async ([, layer]) => {
    if (!ctx.layers.has(layer)) return { total: 0, near: 0 };
    const wires = await queryFeatures(ctx, layer);
    const near = wires.filter((wire) => wire.geometry && geometryEngine.intersects(buffer, wire.geometry)).length;
    return { total: wires.length, near };
  }));
  const total = perLayer.reduce((sum, entry) => sum + entry.total, 0);
  const near = perLayer.reduce((sum, entry) => sum + entry.near, 0);
  if (!total) throw new Error('no wires');
  return { kind: 'kpi', value: `${near}`, help: `Wire spans within ${VEG_ENCROACH_FT} ft of vegetation (of ${total})` };
};

type ComputeFn = (ctx: MetricsContext) => Promise<WidgetData>;

const COMPUTE: Record<string, ComputeFn> = {
  g_total_features: gTotalFeatures,
  g_layers: gLayers,
  g_highlights: gHighlights,
  g_top_layers: gTopLayers,
  g_largest_area: gLargestArea,
  g_composition: gComposition,
  g_geom_types: gGeometryTypes,
  g_size_dist: gSizeDist,
  g_complexity: gComplexity,
  g_categories: gCategories,
  g_category_radar: gCategoryRadar,
  g_category_stacked: gCategoryStacked,
  g_inventory: gInventory,
  tele_poles: telePoles,
  tele_pole_height: telePoleHeight,
  tele_wire_len: teleWireLen,
  tele_attach: teleAttach,
  tele_clear_flags: teleClearFlags,
  tele_compliance: teleCompliance,
  tele_pole_hist: telePoleHist,
  tele_wire_bar: teleWireBar,
  tele_clear_bands: teleClearBands,
  tele_low_sags: teleLowSags,
  tele_pole_loading: telePoleLoading,
  tele_attach_types: teleAttachTypes,
  tele_attach_dist: teleAttachDist,
  tele_attach_heatmap: teleAttachHeatmap,
  tele_violations: teleViolations,
  tele_veg_encroach: vegEncroachment,
  g_data_quality: dataQuality,
  aec_site_area: aecSiteArea,
  aec_impervious: aecImpervious,
  aec_impervious_gauge: aecImperviousGauge,
  aec_barriers: aecBarriers,
  aec_drainage: aecDrainage,
  aec_driveway_bar: aecDrivewayBar,
  aec_cover_donut: aecCoverDonut,
  aec_imperv_waterfall: aecImperviousWaterfall,
};

// Generic widgets first (always applicable), then curated ones gated by `requires`.
export const GENERIC_WIDGETS: WidgetDef[] = [
  { id: 'g_total_features', title: 'Total features', persona: 'Site', kind: 'kpi' },
  { id: 'g_layers', title: 'Layers', persona: 'Site', kind: 'kpi' },
  { id: 'g_highlights', title: 'Interesting facts', persona: 'Site', kind: 'facts' },
  { id: 'g_top_layers', title: 'Largest layers', persona: 'Site', kind: 'bar' },
  { id: 'g_largest_area', title: 'Largest areas (acres)', persona: 'Site', kind: 'bar' },
  { id: 'g_composition', title: 'Layer composition', persona: 'Site', kind: 'treemap' },
  { id: 'g_geom_types', title: 'Geometry types', persona: 'Site', kind: 'donut' },
  { id: 'g_size_dist', title: 'Polygon size distribution', persona: 'Site', kind: 'hist' },
  { id: 'g_complexity', title: 'Layer complexity', persona: 'Site', kind: 'bubble' },
  { id: 'g_categories', title: 'Features by category', persona: 'Site', kind: 'table' },
  { id: 'g_category_radar', title: 'Category mix', persona: 'Site', kind: 'radar' },
  { id: 'g_category_stacked', title: 'Geometry mix by category', persona: 'Site', kind: 'stacked' },
  { id: 'g_inventory', title: 'Layer inventory', persona: 'Site', kind: 'table' },
  { id: 'g_data_quality', title: 'Data quality', persona: 'Site', kind: 'facts' },
];

export const CURATED_WIDGETS: WidgetDef[] = [
  { id: 'tele_poles', title: 'Poles', persona: 'Telecom', kind: 'kpi', requires: 'T_UP_BOTTOM' },
  { id: 'tele_pole_height', title: 'Pole height', persona: 'Telecom', kind: 'kpi', requires: 'T_UP_BOTTOM' },
  { id: 'tele_wire_len', title: 'Total wire', persona: 'Telecom', kind: 'kpi', requires: 'T_COMM_WIRE' },
  { id: 'tele_attach', title: 'Attachments', persona: 'Telecom', kind: 'kpi', requires: 'T_COMM_POA' },
  { id: 'tele_clear_flags', title: 'Clearance flags', persona: 'Telecom', kind: 'kpi', requires: 'ROAD_LINE_CLEARANCE' },
  { id: 'tele_compliance', title: 'Clearance compliance', persona: 'Telecom', kind: 'radial', requires: 'ROAD_LINE_CLEARANCE' },
  { id: 'tele_pole_hist', title: 'Pole-height distribution', persona: 'Telecom', kind: 'hist', requires: 'T_UP_BOTTOM' },
  { id: 'tele_wire_bar', title: 'Wire length by type', persona: 'Telecom', kind: 'bar', requires: 'T_COMM_WIRE' },
  { id: 'tele_clear_bands', title: 'Road-clearance bands', persona: 'Telecom', kind: 'bar', requires: 'ROAD_LINE_CLEARANCE' },
  { id: 'tele_low_sags', title: 'Lowest line sags', persona: 'Telecom', kind: 'table', requires: 'LOWEST_LINE_SAG' },
  { id: 'tele_pole_loading', title: 'Pole loading', persona: 'Telecom', kind: 'kpi', requires: 'T_UP_BOTTOM' },
  { id: 'tele_attach_types', title: 'Attachments by type', persona: 'Telecom', kind: 'bar', requires: 'T_UP_BOTTOM' },
  { id: 'tele_attach_dist', title: 'Attachments per pole', persona: 'Telecom', kind: 'hist', requires: 'T_UP_BOTTOM' },
  { id: 'tele_attach_heatmap', title: 'Attachment height × type', persona: 'Telecom', kind: 'heatmap', requires: 'T_UP_BOTTOM' },
  { id: 'tele_violations', title: 'Clearance violations', persona: 'Telecom', kind: 'table', requires: 'ROAD_LINE_CLEARANCE' },
  { id: 'tele_veg_encroach', title: 'Vegetation encroachment', persona: 'Telecom', kind: 'kpi', requires: 'V_VEGETATION' },
  { id: 'aec_site_area', title: 'Site area', persona: 'AEC', kind: 'kpi', requires: 'A_BOUNDARY' },
  { id: 'aec_impervious', title: 'Impervious area', persona: 'AEC', kind: 'kpi', requires: 'A_BOUNDARY' },
  { id: 'aec_impervious_gauge', title: 'Impervious share', persona: 'AEC', kind: 'radial', requires: 'A_BOUNDARY' },
  { id: 'aec_barriers', title: 'Fence/railing', persona: 'AEC', kind: 'kpi', requires: 'S_FENCE' },
  { id: 'aec_drainage', title: 'Drainage points', persona: 'AEC', kind: 'kpi', requires: 'U_CATCHBASIN' },
  { id: 'aec_driveway_bar', title: 'Driveway area by surface', persona: 'AEC', kind: 'bar', requires: 'R_DRIVEWAY_ASPH' },
  { id: 'aec_cover_donut', title: 'Surface cover', persona: 'AEC', kind: 'donut', requires: 'A_BOUNDARY' },
  { id: 'aec_imperv_waterfall', title: 'Site area breakdown', persona: 'AEC', kind: 'waterfall', requires: 'A_BOUNDARY' },
];

const ALL_WIDGETS = [...GENERIC_WIDGETS, ...CURATED_WIDGETS];
const WIDGET_BY_ID = new Map(ALL_WIDGETS.map((widget) => [widget.id, widget]));

// Widgets relevant to THIS gdb: generic ones + curated ones whose required layer is loaded.
export const applicableWidgets = (layers: Map<string, QueryableLayer>): WidgetDef[] => [
  ...GENERIC_WIDGETS,
  ...CURATED_WIDGETS.filter((widget) => !widget.requires || layers.has(widget.requires)),
];

export const getWidgetDef = (id: string): WidgetDef | undefined => WIDGET_BY_ID.get(id);

// Compute one widget's render-ready data; never throws (returns null so one broken widget
// doesn't sink the board).
export const computeWidget = async (id: string, ctx: MetricsContext): Promise<WidgetData | null> => {
  const compute = COMPUTE[id];
  if (!compute) return null;
  try {
    return await compute(ctx);
  } catch (err) {
    warnMetric(`widget ${id}`, err);
    return null;
  }
};

// ============================================================================================
// END LEGACY auto-widget block. Everything BELOW is Khora-driven — build new work HERE.
// (focusedDistribution/focusedRelationship/focusedWidgets further down are also LEGACY — see note.)
// ============================================================================================
// Khora-driven widgets the USER pins from a Khora answer (add_dashboard_widget). `computeSpec` computes
// one from its spec against the live layers, view-scoped ("Current view only"). This is the ONLY
// dashboard-widget path we extend.

export type WidgetOperation = 'count' | 'length_ft' | 'area_sqft' | 'count_by' | 'average' | 'sum' | 'min' | 'max';

// Attribute filter a Khora widget can carry (e.g. poles under 30 ft: {field: 'Height_ft', op: '<',
// value: 30}). Applied client-side after a full-field fetch, with the field resolved against the
// published layer's renamed fields — a raw SQL where-string would silently miss renamed columns.
export interface WidgetFilter {
  field: string;
  op: '<' | '<=' | '>' | '>=' | '=' | '!=';
  value: number | string;
}

// A live, recomputable widget the chatbot pinned — mirrors the agent's add_dashboard_widget spec.
export interface WidgetSpec {
  id: string;
  title: string;
  kind: WidgetKind;
  operation: WidgetOperation;
  layers: string[];
  byField?: string;
  // Numeric field to aggregate for average/sum/min/max — resolved fuzzily against the layer's real fields.
  field?: string;
  // Optional attribute filter scoping every operation to matching features only.
  filter?: WidgetFilter | null;
  // Plain-language "how it's computed", from the Lambda — shown in the widget's ℹ️ details popover.
  derivation?: string;
  // Requested display unit for a length_ft/area_sqft widget (e.g. 'm', 'ac'). The value is always
  // measured in feet, then converted + labeled in this unit to match Khora's chat answer; absent = feet.
  displayUnit?: string;
  // The Lambda's whole-gdb snapshot (the value Khora reported in chat). Rendered as the pinned
  // widget's value when NOT in "current view only" mode, so the dashboard matches Khora's answer
  // instead of re-deriving from the published viewer layer — which can diverge from the gdb.
  preview?: { value?: number; entries?: [string, number][] } | null;
  // Org-broadcast share marker: set to the gdb id this widget is shared under.
  sharedGdbId?: string;
}

// Build the render-ready WidgetData for a widget from the Lambda's whole-gdb snapshot `preview`, using
// the SAME shapes computeSpec produces so the inline chat preview matches the eventually-pinned widget.
export const widgetPreviewData = (
  spec: { operation: WidgetOperation; kind: WidgetKind; byField?: string; field?: string; displayUnit?: string },
  preview?: { value?: number; entries?: [string, number][] } | null,
): WidgetData | null => {
  if (!preview) return null;
  if (spec.operation === 'count_by') {
    const entries = preview.entries ?? [];
    if (!entries.length) return null;
    if (spec.kind === 'donut') return { kind: 'donut', labels: entries.map((e) => e[0]), values: entries.map((e) => e[1]) };
    if (spec.kind === 'table') return { kind: 'table', rows: entries.map(([key, value]) => ({ [spec.byField ?? 'Category']: key, Count: value })) };
    return { kind: 'bar', x: entries.map((e) => e[0]), y: entries.map((e) => e[1]), xTitle: spec.byField ?? 'Category', yTitle: 'Count' };
  }
  if (typeof preview.value !== 'number') return null;
  const help = spec.operation === 'count' ? '' : `${spec.operation} · ${spec.field ?? ''}`;
  // Length/area snapshots are in feet — convert + label in the widget's display_unit (matching Khora's
  // chat answer) instead of showing a bare number; other scalars have no geodesic unit.
  if (spec.operation === 'length_ft') return { kind: 'kpi', value: formatMeasure(preview.value, 'length', spec.displayUnit), help };
  if (spec.operation === 'area_sqft') return { kind: 'kpi', value: formatMeasure(preview.value, 'area', spec.displayUnit), help };
  // average/sum/min/max of a *_ft field may carry a length display_unit; count is dimensionless.
  return { kind: 'kpi', value: formatScalar(preview.value, spec.displayUnit), help };
};

const countBy = async (ctx: MetricsContext, layers: string[], field: string): Promise<[string, number][]> => {
  const counts = new Map<string, number>();
  await Promise.all(layers.map(async (name) => {
    // Resolve `field` against THIS layer's real fields: published GeoJSON renames columns
    // (Pole_ID -> "Pole ID"), so grouping by the raw name would read undefined for every feature and
    // collapse the whole layer into one "—" bucket. Mirrors the filtered count_by path.
    const grouped = resolveField(ctx.layers.get(name), field);
    const features = await queryFeatures(ctx, name, ['*'], false); // all attrs, no geometry — grouping only
    features.forEach((feature) => {
      const value = feature.attributes?.[grouped];
      const key = value === null || value === undefined ? '—' : String(value);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
  }));
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
};

// A layer's features passing the widget filter, with the filter field resolved against the published
// layer's renamed fields (Height_ft -> "Height (Ft)").
const filteredFeatures = async (ctx: MetricsContext, layerName: string, filter: WidgetFilter): Promise<__esri.Graphic[]> => {
  const field = resolveField(ctx.layers.get(layerName), filter.field);
  const features = await queryFeatures(ctx, layerName); // all fields — renamed fields read reliably
  return features.filter((feature) => matchesFilter(feature.attributes?.[field], filter.op, filter.value));
};

// Filtered geodesic total (ft / ft²) — the filtered twin of sumLengthFt/sumAreaSqft, measuring only
// the matching features, still clipped to the view in view-only mode.
const filteredMeasure = async (ctx: MetricsContext, layers: string[], filter: WidgetFilter, kind: 'length' | 'area'): Promise<number> => {
  const clip = viewClip(ctx);
  let total = 0;
  await Promise.all(layers.map(async (name) => {
    (await filteredFeatures(ctx, name, filter)).forEach((feature) => {
      const geometry = feature.geometry && clipped(feature.geometry, clip);
      if (!geometry) return;
      total += kind === 'length'
        ? geometryEngine.geodesicLength(geometry, 'feet')
        : Math.abs(geometryEngine.geodesicArea(geometry as __esri.Polygon, 'square-feet'));
    });
  }));
  return total;
};

const filterHelp = (filter?: WidgetFilter | null): string => (
  filter ? ` · ${filter.field} ${filter.op} ${filter.value}` : ''
);

// Compute a chatbot-pinned custom widget against the loaded layers; never throws.
export const computeSpec = async (spec: WidgetSpec, ctx: MetricsContext): Promise<WidgetData | null> => {
  const { filter } = spec;
  try {
    if (spec.operation === 'count') {
      const snap = spec.preview?.value;
      const total = preferSnapshot(snap, ctx.viewOnly)
        ? snap
        : (await Promise.all(spec.layers.map(async (layer) => (
          filter ? (await filteredFeatures(ctx, layer, filter)).length : count(ctx, layer))))).reduce((a, b) => a + b, 0);
      return { kind: 'kpi', value: fmt(total), help: `${spec.layers.join(', ')}${filterHelp(filter)}` };
    }
    if (spec.operation === 'length_ft') {
      const snap = spec.preview?.value;
      let total: number;
      if (preferSnapshot(snap, ctx.viewOnly)) total = snap;
      else total = filter ? await filteredMeasure(ctx, spec.layers, filter, 'length') : await sumLengthFt(ctx, spec.layers);
      return { kind: 'kpi', value: formatMeasure(total, 'length', spec.displayUnit), help: `${spec.layers.join(', ')}${filterHelp(filter)}` };
    }
    if (spec.operation === 'area_sqft') {
      const snap = spec.preview?.value;
      let total: number;
      if (preferSnapshot(snap, ctx.viewOnly)) total = snap;
      else total = filter ? await filteredMeasure(ctx, spec.layers, filter, 'area') : await sumAreaSqft(ctx, spec.layers);
      return { kind: 'kpi', value: formatMeasure(total, 'area', spec.displayUnit), help: `${spec.layers.join(', ')}${filterHelp(filter)}` };
    }
    if (spec.operation === 'count_by' && spec.byField) {
      const { byField } = spec;
      let entries: [string, number][];
      if (filter) {
        const counts = new Map<string, number>();
        await Promise.all(spec.layers.map(async (name) => {
          const grouped = resolveField(ctx.layers.get(name), byField);
          (await filteredFeatures(ctx, name, filter)).forEach((feature) => {
            const value = feature.attributes?.[grouped];
            const key = value === null || value === undefined ? '—' : String(value);
            counts.set(key, (counts.get(key) || 0) + 1);
          });
        }));
        entries = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
      } else {
        entries = await countBy(ctx, spec.layers, byField);
      }
      const selectors = entries.map(([key]): ChartSelector => ({ type: 'category', layers: spec.layers, field: byField, value: key }));
      if (spec.kind === 'donut') return { kind: 'donut', labels: entries.map((e) => e[0]), values: entries.map((e) => e[1]), selectors };
      if (spec.kind === 'table') return { kind: 'table', rows: entries.map(([key, value]) => ({ [byField]: key, Count: value })) };
      return { kind: 'bar', x: entries.map((e) => e[0]), y: entries.map((e) => e[1]), xTitle: byField, yTitle: 'Count', selectors };
    }
    // Scalar aggregates of a numeric field (average/sum/min/max) — a KPI card. numbersAttr resolves the
    // renamed field and reads it across every named layer; min/max use reduce (no spread) to stay safe on
    // large layers.
    if (spec.field && (spec.operation === 'average' || spec.operation === 'sum' || spec.operation === 'min' || spec.operation === 'max')) {
      const { field } = spec;
      const snap = spec.preview?.value;
      if (preferSnapshot(snap, ctx.viewOnly)) {
        return { kind: 'kpi', value: formatScalar(snap, spec.displayUnit), help: `${spec.operation} · ${field}${filterHelp(filter)}` };
      }
      const values = (await Promise.all(spec.layers.map(async (layer) => {
        if (!filter) return numbersAttr(ctx, layer, field);
        const resolved = resolveField(ctx.layers.get(layer), field);
        return (await filteredFeatures(ctx, layer, filter))
          .map((feature) => feature.attributes?.[resolved])
          .filter((value): value is number => typeof value === 'number');
      }))).flat();
      if (!values.length) return { kind: 'kpi', value: '—', help: `${field}: no numeric values` };
      let result: number;
      if (spec.operation === 'sum') result = values.reduce((a, b) => a + b, 0);
      else if (spec.operation === 'min') result = values.reduce((a, b) => Math.min(a, b), Infinity);
      else if (spec.operation === 'max') result = values.reduce((a, b) => Math.max(a, b), -Infinity);
      else result = values.reduce((a, b) => a + b, 0) / values.length; // average
      return { kind: 'kpi', value: formatScalar(result, spec.displayUnit), help: `${spec.operation} · ${field}${filterHelp(filter)}` };
    }
    return null;
  } catch (err) {
    warnMetric(`kpi ${spec.operation} ${spec.field ?? ''}`, err);
    return null;
  }
};

// The features behind a clicked count_by bar/donut datapoint: those whose `field` attribute equals
// the clicked category `label`, grouped by layer (for layerView.highlight). Mirrors countBy's exact
// attribute match (String(value), '—' for null) so the selection matches what the chart counted.
export const featuresForCategory = async (ctx: MetricsContext, layers: string[], field: string, label: string): Promise<{ layer: QueryableLayer; features: __esri.Graphic[] }[]> => {
  const groups: { layer: QueryableLayer; features: __esri.Graphic[] }[] = [];
  await Promise.all(layers.map(async (name) => {
    const layer = ctx.layers.get(name);
    if (!layer) return;
    // Resolve the group field per layer, exactly like countBy built the categories — otherwise a
    // renamed field (Pole_ID -> "Pole ID") reads undefined here and the click highlights nothing.
    const grouped = resolveField(layer, field);
    const features = await queryFeatures(ctx, name); // all fields + geometry — needed to highlight
    const matched = features.filter((feature) => {
      const value = feature.attributes?.[grouped];
      const key = value === null || value === undefined ? '—' : String(value);
      return key === label;
    });
    if (matched.length) groups.push({ layer, features: matched });
  }));
  return groups;
};

// Resolve a clicked datapoint's selector into the features to highlight (grouped by layer).
export const featuresForSelector = async (ctx: MetricsContext, selector: ChartSelector): Promise<{ layer: QueryableLayer; features: __esri.Graphic[] }[]> => {
  if (selector.type === 'category') return featuresForCategory(ctx, selector.layers, selector.field, selector.value);
  if (selector.type === 'layers') {
    const groups: { layer: QueryableLayer; features: __esri.Graphic[] }[] = [];
    await Promise.all(selector.layers.map(async (name) => {
      const layer = ctx.layers.get(name);
      if (!layer) return;
      const features = await queryFeatures(ctx, name);
      if (features.length) groups.push({ layer, features });
    }));
    return groups;
  }
  // 'feature': fetch exactly that feature by objectId — robust to large layers (a client-side filter
  // over a maxRecordCount-capped query can miss it) and to the current view extent (we fly to it).
  const layer = ctx.layers.get(selector.layer);
  if (!layer) return [];
  const query = layer.createQuery();
  query.objectIds = [selector.objectId];
  query.returnGeometry = true;
  try {
    const result = await layer.queryFeatures(query);
    return result.features.length ? [{ layer, features: result.features }] : [];
  } catch (err) {
    warnMetric('layer feature query', err);
    return [];
  }
};

// Histogram-bin click: features of `layer` whose `field` is in [min, max) (inclusive of max on the
// last bin, matching how the chart buckets values).
export const featuresForRange = async (ctx: MetricsContext, layer: string, field: string, min: number, max: number, inclusiveMax: boolean): Promise<{ layer: QueryableLayer; features: __esri.Graphic[] }[]> => {
  const target = ctx.layers.get(layer);
  if (!target) return [];
  const features = await queryFeatures(ctx, layer); // all fields — renamed fields read reliably
  const matched = features.filter((feature) => {
    const value = feature.attributes?.[field];
    return typeof value === 'number' && value >= min && (inclusiveMax ? value <= max : value < max);
  });
  return matched.length ? [{ layer: target, features: matched }] : [];
};

// --------------------------------------------------------------------------- //
// Single-layer focus (layer dropdown / clicking a feature on the map)         //
// --------------------------------------------------------------------------- //

const NUMERIC_FIELD_TYPES = ['small-integer', 'integer', 'single', 'double', 'long', 'big-integer'];
const SKIP_FIELD = /objectid|globalid|shape|^fid$|_id$/i;
// For scatter/correlation we want measured quantities, not ids, coordinates, or elevation.
// ============================================================================================
// LEGACY (cont.) — the per-layer "focus" auto-widgets (focusedDistribution/focusedRelationship/
// focusedWidgets + pickField). Driven ONLY by LEGACY_AUTO_WIDGETS's layer dropdown. Frozen; do not
// extend. featuresForCategory/Selector/Range ABOVE are NOT legacy — they power Khora chart-click.
// ============================================================================================
const NON_MEASURE_FIELD = /objectid|globalid|shape|fid|id$|long|lat|lon|elev|northing|easting/i;

const pickField = (layer: QueryableLayer, accept: (type: string) => boolean) => (
  (layer.fields || []).find((field) => accept(field.type) && !SKIP_FIELD.test(field.name))
);

// Distribution chart for the focused layer: a histogram of its first numeric field, else a
// top-values bar of its first text field, else nothing.
const focusedDistribution = async (
  ctx: MetricsContext,
  layer: QueryableLayer,
  name: string,
  color: string,
): Promise<{ id: string; title: string; data: WidgetData } | null> => {
  const numeric = pickField(layer, (type) => NUMERIC_FIELD_TYPES.includes(type));
  if (numeric) {
    const values = await numbersAttr(ctx, name, numeric.name);
    if (values.length) {
      const label = numeric.alias || numeric.name;
      return { id: `${name}-dist`, title: `${label} distribution`, data: { kind: 'hist', values, xTitle: label, source: { layer: name, field: numeric.name } } };
    }
  }
  const text = pickField(layer, (type) => type === 'string');
  if (text) {
    // Top categories + an "Other" remainder so the bar still sums to the layer total (never silently drop).
    const allCats = await countBy(ctx, [name], text.name);
    const TOP_CATS = 8;
    const otherSum = allCats.slice(TOP_CATS).reduce((sum, [, categoryCount]) => sum + categoryCount, 0);
    const entries: [string, number][] = otherSum > 0
      ? [...allCats.slice(0, TOP_CATS), ['Other', otherSum] as [string, number]]
      : allCats;
    if (entries.length) {
      const label = text.alias || text.name;
      return {
        id: `${name}-dist`,
        title: `By ${label}`,
        data: {
          kind: 'bar',
          x: entries.map((entry) => entry[0]),
          y: entries.map((entry) => entry[1]),
          xTitle: label,
          yTitle: 'Count',
          colors: entries.map(() => color),
          selectors: entries.map(([value]): ChartSelector => ({ type: 'category', layers: [name], field: text.name, value })),
        },
      };
    }
  }
  return null;
};

const fieldLabel = (field: __esri.Field): string => field.alias || field.name;

// A focused layer's measured (non-id/coordinate) numeric fields surface attribute relationships:
// the first three give a bubble chart (x, y, size), the first two a scatter. Returns null when
// there aren't enough measures or values to be meaningful.
const focusedRelationship = async (
  ctx: MetricsContext,
  layer: QueryableLayer,
  name: string,
): Promise<{ id: string; title: string; data: WidgetData } | null> => {
  const measures = (layer.fields || []).filter(
    (field) => NUMERIC_FIELD_TYPES.includes(field.type) && !NON_MEASURE_FIELD.test(field.name),
  );
  if (measures.length < 2) return null;
  if (measures.length >= 3) {
    const [fx, fy, fz] = measures;
    // Include the objectId field so getObjectId() resolves — otherwise points have no feature id to
    // highlight, and we must NOT drop them for it (filter only on the plotted x/y/z values).
    const features = await queryFeatures(ctx, name, [fx.name, fy.name, fz.name, layer.objectIdField].filter(Boolean));
    const rows = features
      .map((feature) => ({
        x: feature.attributes?.[fx.name],
        y: feature.attributes?.[fy.name],
        z: feature.attributes?.[fz.name],
        oid: feature.getObjectId(),
      }))
      .filter((row): row is { x: number; y: number; z: number; oid: number } => (
        typeof row.x === 'number' && typeof row.y === 'number' && typeof row.z === 'number'
      ));
    if (rows.length < 3) return null;
    return {
      id: `${name}-bubble`,
      title: `${fieldLabel(fx)} vs ${fieldLabel(fy)} (size: ${fieldLabel(fz)})`,
      data: {
        kind: 'bubble',
        points: rows.map(({ x, y, z }) => ({ x, y, z })),
        xTitle: fieldLabel(fx),
        yTitle: fieldLabel(fy),
        zTitle: fieldLabel(fz),
        selectors: rows.map((row): ChartSelector | null => (typeof row.oid === 'number' ? { type: 'feature', layer: name, objectId: row.oid } : null)),
      },
    };
  }
  const [fx, fy] = measures;
  const features = await queryFeatures(ctx, name, [fx.name, fy.name, layer.objectIdField].filter(Boolean));
  const rows = features
    .map((feature) => ({ x: feature.attributes?.[fx.name], y: feature.attributes?.[fy.name], oid: feature.getObjectId() }))
    .filter((row): row is { x: number; y: number; oid: number } => typeof row.x === 'number' && typeof row.y === 'number');
  if (rows.length < 3) return null;
  return {
    id: `${name}-scatter`,
    title: `${fieldLabel(fx)} vs ${fieldLabel(fy)}`,
    data: {
      kind: 'scatter',
      points: rows.map(({ x, y }) => ({ x, y })),
      xTitle: fieldLabel(fx),
      yTitle: fieldLabel(fy),
      selectors: rows.map((row): ChartSelector | null => (typeof row.oid === 'number' ? { type: 'feature', layer: name, objectId: row.oid } : null)),
    },
  };
};

// Widgets scoped to ONE layer: feature count, its geometric measure (length for lines, area for
// polygons — clipped to the view in "current view only"), and an attribute distribution.
export const focusedWidgets = async (
  ctx: MetricsContext,
  name: string,
): Promise<{ id: string; title: string; data: WidgetData | null }[]> => {
  const layer = ctx.layers.get(name);
  if (!layer) return [];
  const widgets: { id: string; title: string; data: WidgetData | null }[] = [];
  const geometryType = layer.geometryType || '';
  const color = layerColor(layer) ?? FALLBACK_COLORS[0];
  const scope = ctx.viewOnly ? ' (in view)' : '';

  widgets.push({
    id: `${name}-count`,
    title: 'Features',
    data: { kind: 'kpi', value: fmt(await count(ctx, name)), help: `${name}${scope}` },
  });

  if (geometryType.includes('polygon')) {
    const area = await sumAreaSqft(ctx, [name]);
    widgets.push({
      id: `${name}-area`,
      title: 'Area',
      data: { kind: 'kpi', value: `${fmt(area)} ft²`, help: `${(area / SQFT_PER_ACRE).toFixed(2)} acres${scope}` },
    });
  } else if (geometryType.includes('line')) {
    const length = await sumLengthFt(ctx, [name]);
    widgets.push({
      id: `${name}-length`,
      title: 'Length',
      data: { kind: 'kpi', value: `${fmt(length)} ft`, help: `Total length${scope}` },
    });
  }

  const distribution = await focusedDistribution(ctx, layer, name, color);
  if (distribution) widgets.push(distribution);

  const relationship = await focusedRelationship(ctx, layer, name);
  if (relationship) widgets.push(relationship);

  return widgets;
};

// --------------------------------------------------------------------------- //
// Transparency: the data table behind a chart + how the chart is derived      //
// --------------------------------------------------------------------------- //

// 8 equal-width bins, labeled by range — matches the histogram rendering so the table agrees with
// the chart the user sees.
const histogramBins = (values: number[]): { label: string; count: number }[] => {
  if (!values.length) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const binCount = 8;
  const width = (max - min) / binCount || 1;
  const bins = Array.from({ length: binCount }, (_, index) => ({
    label: `${(min + index * width).toFixed(1)}–${(min + (index + 1) * width).toFixed(1)}`,
    count: 0,
  }));
  values.forEach((value) => {
    const index = Math.min(binCount - 1, Math.floor((value - min) / width));
    bins[index].count += 1;
  });
  return bins;
};

// The tabular data behind a chart — the rows the user can inspect. Returns null when there's no
// meaningful table (or no data).
export const dataTableForWidget = (data: WidgetData | null): { columns: string[]; rows: (string | number)[][] } | null => {
  if (!data) return null;
  if (data.kind === 'bar') {
    return data.x.length ? { columns: [data.xTitle, data.yTitle], rows: data.x.map((label, i) => [label, data.y[i]]) } : null;
  }
  if (data.kind === 'hist') {
    const bins = histogramBins(data.values);
    return bins.length ? { columns: [`${data.xTitle} (range)`, 'Count'], rows: bins.map((bin) => [bin.label, bin.count]) } : null;
  }
  if (data.kind === 'donut') {
    return data.labels.length ? { columns: ['Category', 'Value'], rows: data.labels.map((label, i) => [label, data.values[i]]) } : null;
  }
  if (data.kind === 'treemap') {
    return data.nodes.length ? { columns: ['Name', data.valueTitle], rows: data.nodes.map((node) => [node.name, node.value]) } : null;
  }
  if (data.kind === 'radar') {
    return data.axes.length ? { columns: ['Axis', data.valueTitle], rows: data.axes.map((axis, i) => [axis, data.values[i]]) } : null;
  }
  if (data.kind === 'scatter') {
    return data.points.length ? { columns: [data.xTitle, data.yTitle], rows: data.points.map((point) => [point.x, point.y]) } : null;
  }
  if (data.kind === 'bubble') {
    return data.points.length ? { columns: [data.xTitle, data.yTitle, data.zTitle], rows: data.points.map((point) => [point.x, point.y, point.z]) } : null;
  }
  if (data.kind === 'waterfall') {
    return data.steps.length ? { columns: ['Step', data.valueTitle], rows: data.steps.map((step) => [step.label, step.value]) } : null;
  }
  if (data.kind === 'stacked') {
    return data.categories.length
      ? { columns: ['Category', ...data.series.map((s) => s.name)], rows: data.categories.map((category, i) => [category, ...data.series.map((s) => s.values[i])]) }
      : null;
  }
  if (data.kind === 'facts') {
    return data.items.length ? { columns: ['Fact', 'Value'], rows: data.items.map((item) => [item.label, item.value]) } : null;
  }
  if (data.kind === 'radial') {
    return { columns: ['Metric', 'Value'], rows: [['Value', data.valueLabel], ['Max', String(data.max)]] };
  }
  if (data.kind === 'kpi') {
    return { columns: ['Value', 'Detail'], rows: [[data.value, data.help]] };
  }
  if (data.kind === 'table') {
    return data.rows.length
      ? { columns: Object.keys(data.rows[0]), rows: data.rows.map((row) => Object.keys(data.rows[0]).map((column) => row[column])) }
      : null;
  }
  if (data.kind === 'heatmap') {
    return data.rows.length
      ? { columns: [data.rowTitle, ...data.cols], rows: data.rows.map((rowLabel, i) => [rowLabel, ...data.matrix[i]]) }
      : null;
  }
  return null;
};

// Plain-language notes for widgets whose derivation isn't obvious from the chart's fields alone.
const WIDGET_METHOD_NOTES: Record<string, string> = {
  tele_pole_hist: 'Pole height = the vertical extent of each pole\'s 3D line geometry (top Z − base Z), in feet; if the geometry lacks Z it falls back to the published "Height (Ft)" attribute.',
  tele_pole_height: 'Same pole-height source as the distribution: geometry Z-extent, with the "Height (Ft)" attribute as a fallback.',
  tele_pole_loading: 'Read from each pole\'s inline POA_1..POA_20 attachment columns on T_UP_BOTTOM (an _ID/_Ht/_Type set per attachment).',
  veg_encroachment: 'Vegetation polygons are buffered and intersected against wires/poles via geometryEngine; an intersection counts as an encroachment.',
};

// Source layer(s) + grouping field a chart's selectors map back to.
const selectorSource = (selectors?: (ChartSelector | null)[]): { layers: string[]; field?: string } => {
  const present = (selectors || []).filter(Boolean) as ChartSelector[];
  const layers = new Set<string>();
  let field: string | undefined;
  present.forEach((selector) => {
    if (selector.type === 'layers') {
      selector.layers.forEach((name) => layers.add(name));
    } else if (selector.type === 'category') {
      selector.layers.forEach((name) => layers.add(name));
      field = selector.field;
    } else if (selector.type === 'feature') {
      layers.add(selector.layer);
    }
  });
  return { layers: Array.from(layers), field };
};

// Plain-language explanation of how a chart is produced from the geodatabase — shown behind the
// "i" button so the mapping from data to graph is never a black box.
export const describeWidget = (data: WidgetData | null, id: string): string[] => {
  if (!data) return [];
  const lines: string[] = [];
  if (WIDGET_METHOD_NOTES[id]) lines.push(WIDGET_METHOD_NOTES[id]);

  if (data.kind === 'bar') {
    const { layers, field } = selectorSource(data.selectors);
    if (field && layers.length) lines.push(`Layer ${layers.join(', ')} grouped by "${field}". Each bar = number of features per group (${data.yTitle}).`);
    else if (layers.length) lines.push(`One bar per layer: ${layers.join(', ')}. Bar height = ${data.yTitle}.`);
    else lines.push(`Each bar = ${data.yTitle} by ${data.xTitle}.`);
  } else if (data.kind === 'hist') {
    const source = data.source ? `${data.source.layer}.${data.source.field}` : data.xTitle;
    lines.push(`Histogram of ${source}: values split into 8 equal-width bins; bar height = number of features in each bin.`);
  } else if (data.kind === 'donut') {
    const { layers } = selectorSource(data.selectors);
    lines.push(`Each slice = a share of the total${layers.length ? ` across ${layers.join(', ')}` : ''}.`);
  } else if (data.kind === 'scatter') {
    const { layers } = selectorSource(data.selectors);
    lines.push(`Each point = one feature${layers.length ? ` of ${layers.join(', ')}` : ''}, plotted as (${data.xTitle}, ${data.yTitle}).`);
  } else if (data.kind === 'bubble') {
    const { layers } = selectorSource(data.selectors);
    lines.push(`Each bubble = one feature${layers.length ? ` of ${layers.join(', ')}` : ''}: x = ${data.xTitle}, y = ${data.yTitle}, size = ${data.zTitle}.`);
  } else if (data.kind === 'radial') {
    lines.push(`Gauge: ${data.valueLabel} out of ${data.max}. ${data.help}`);
  } else if (data.kind === 'treemap') {
    lines.push(`Each tile = a category sized by ${data.valueTitle}.`);
  } else if (data.kind === 'radar') {
    lines.push(`Each axis = ${data.valueTitle} for that category.`);
  } else if (data.kind === 'stacked') {
    lines.push(`Bars measured by ${data.valueTitle}, split into: ${data.series.map((s) => s.name).join(', ')}.`);
  } else if (data.kind === 'waterfall') {
    lines.push(`Cumulative ${data.valueTitle} built up step by step.`);
  } else if (data.kind === 'heatmap') {
    lines.push(`A 2D histogram. Each cell = ${data.valueTitle} for that ${data.rowTitle} (rows) within that ${data.colTitle} band (columns); a deeper blue means a higher count.`);
  } else if (data.kind === 'kpi') {
    lines.push(data.help || 'A single computed metric.');
  }

  const clickable = ['bar', 'donut', 'scatter', 'bubble', 'hist'].includes(data.kind);
  if (clickable) lines.push('Click a datapoint to highlight the matching feature(s) on the map.');
  return lines;
};
